import { datadogLogs } from '@datadog/browser-logs';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useQuery, useQueryOne } from '../../../../loco-sync';
import { BasicModel } from '../../../../loco-sync/typeUtils';
import { trpc } from '../../../../trpc/react';
import { BlockPreview } from '../types';
import {
  mergeWorkoutIdsWithCyclesAndSessions,
  WorkoutIdWithCycleAndSession,
} from '../utils';
import { AnyPublishTask, isNowPastLastTimePlusThreshold } from './utils';

export type PublishState =
  | {
      type: 'pending';
    }
  | {
      type: 'in_progress';
      value: number;
    }
  | {
      type: 'error';
      workoutWithErrors: WorkoutIdWithCycleAndSession[];
    }
  | {
      type: 'stalled';
      task: AnyPublishTask;
    }
  | {
      // The publish message never reached the worker (SC-5458): the task
      // stays visible as a retryable failure, so the coach can retry.
      type: 'send_failed';
      task: AnyPublishTask;
    }
  | {
      type: 'ready';
    };

export function isPublishActionDisabled(state: PublishState): boolean {
  return state.type === 'in_progress' || state.type === 'pending';
}

const PUBLISH_DIAGNOSTIC_MESSAGES = {
  worker_not_started: "Publish task is pending, worker hasn't started yet",
  total_not_set: "Publish task is in progress, worker hasn't set total yet",
} as const;

type PublishDiagnostic = keyof typeof PUBLISH_DIAGNOSTIC_MESSAGES;

export function publishDiagnostic(
  state: PublishState,
  publishTask: BasicModel<'BlockPublishTask'> | undefined,
): PublishDiagnostic | undefined {
  if (state.type === 'pending' && publishTask) {
    return publishTask.status === 'pending'
      ? 'worker_not_started'
      : 'total_not_set';
  }
  // A stale pending row surfaces as stalled (SC-5458): keep the
  // worker_not_started signal firing for exactly that stuck case.
  if (state.type === 'stalled' && publishTask?.status === 'pending') {
    return 'worker_not_started';
  }
  return undefined;
}

export function usePublishState(
  block: BlockPreview,
  hasBeenPublished: boolean,
): PublishState {
  const queryClient = useQueryClient();
  const { data: blockDraftWorkouts } = useQuery('BlockDraftWorkoutLayer', {
    blockId: block.id,
  });
  const { data: publishTask } = useQueryOne('BlockPublishTask', {
    blockId: block.id,
  });

  const [now, setNow] = useState(() => DateTime.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(DateTime.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  const state = publishState(block, blockDraftWorkouts, publishTask, now);

  const diagnostic = publishDiagnostic(state, publishTask);
  const publishTaskId = publishTask?.id;
  const publishTaskStatus = publishTask?.status;
  useEffect(() => {
    if (!diagnostic) {
      return;
    }
    datadogLogs.logger.info(PUBLISH_DIAGNOSTIC_MESSAGES[diagnostic], {
      publishTaskId,
      status: publishTaskStatus,
      blockId: block.id,
    });
  }, [diagnostic, publishTaskId, publishTaskStatus, block.id]);

  // TODO: make this 'onSuccess' potentially compare prev state and cur state
  // in_progress -> ready = success
  if (state?.type === 'ready' && !hasBeenPublished) {
    queryClient.refetchQueries({
      queryKey: getQueryKey(
        trpc.block.blockForDraft,
        { blockId: block.id },
        'query',
      ),
    });
  }

  return state;
}

export function publishState(
  block: BlockPreview,
  blockWorkoutLayers: BasicModel<'BlockDraftWorkoutLayer'>[],
  publishTask: BasicModel<'BlockPublishTask'> | undefined,
  now: DateTime,
): PublishState {
  // No publish task = ready to publish
  if (!publishTask) {
    return { type: 'ready' };
  }

  // Check for stalled (no updates in 30 seconds). Covers pending too
  // (SC-5458 review): a pending row can outlive its message — the send
  // failed and the send_failed marking also failed — so the stall check is
  // its bounded-time recovery: the stalled state keeps Publish enabled and
  // the retry replaces the stuck row.
  const lastChangeTime = publishTask.updatedAt ?? publishTask.createdAt;
  if (
    (publishTask.status === 'pending' ||
      publishTask.status === 'in_progress') &&
    isNowPastLastTimePlusThreshold([lastChangeTime], now)
  ) {
    return {
      type: 'stalled',
      task: publishTask,
    };
  }

  // Pending - just created, worker hasn't started yet
  if (publishTask.status === 'pending') {
    return { type: 'pending' };
  }

  // Send failed - the worker message never went out (SC-5458); the task
  // stays as a visible, retryable failure instead of falling to `ready`.
  if (publishTask.status === 'send_failed') {
    return { type: 'send_failed', task: publishTask };
  }

  // In progress - show percentage
  if (publishTask.status === 'in_progress') {
    if (publishTask.totalWorkouts === 0) {
      return { type: 'pending' }; // Worker hasn't set total yet
    }
    const completed = publishTask.completedWorkoutIds.length;
    const total = publishTask.totalWorkouts;
    return {
      type: 'in_progress',
      value: (completed / total) * 100,
    };
  }

  // Failed or completed - check for errors
  const failedWorkoutIds = Object.keys(publishTask.failedWorkouts || {});

  if (failedWorkoutIds.length > 0) {
    const mergedWorkoutIds = mergeWorkoutIdsWithCyclesAndSessions(
      block,
      blockWorkoutLayers,
    );

    const workoutIdsWithErrors = mergedWorkoutIds.filter((w) =>
      failedWorkoutIds.includes(w.workoutId),
    );

    return {
      type: 'error',
      workoutWithErrors: workoutIdsWithErrors,
    };
  }

  return { type: 'ready' };
}
